import { FC, memo, useMemo } from 'react'
import { HStack, Card, Center, Stack, CardProps, Flex, Divider } from '@chakra-ui/react'

import { CustomIconViewer, Status, NoveltyBadge, Id, TextWithCharacterLimit } from 'components'
import { Role, RoleFieldsName, RoleFilterKeyString, RoleKeyString } from 'Attendances/types'
import { ROLE_INITIAL_FILTER_FIELDS } from 'Attendances/constants/pages/roles'
import { OwnerIcon, CategoryBadge } from 'components/visualHelps'

type Props = CardProps & {
  onlyFields?: RoleFieldsName[]
  rolePrefix?: string
  roleFilter?: RoleFilterKeyString
  roleInformation: Role
}

const RoleCard: FC<Props> = ({ onlyFields, roleInformation, roleFilter, children, ...cardProps }) => {
  const roleFields = useMemo(() => {
    return onlyFields || ROLE_INITIAL_FILTER_FIELDS
  }, [])

  function setRoleValueByKey(key: string): string {
    const roleWithKeyString: RoleKeyString = { ...roleInformation }
    return (roleWithKeyString[`role_${key}`] || roleWithKeyString[key]) as string
  }

  function setRoleFilterValueByKey(key: string): string {
    if (!roleFilter) return ''
    return roleFilter[`role_${key}`] || roleFilter[key]
  }

  return (
    <Card height="100%" width="100%" className="accessible" _hover={{ bg: 'lightPrimary' }} p={2} {...cardProps}>
      <Stack>
        <Flex alignItems="center" gap={1} justifyContent="space-between">
          <Flex alignItems="center" gap={2}>
            <Id id={setRoleValueByKey('id')} />
            <NoveltyBadge date={setRoleValueByKey('created_at')} />
          </Flex>
          <Flex alignItems="center" gap={2}>
            <OwnerIcon entity={roleInformation} />
            <CategoryBadge category={setRoleValueByKey('category')} />
            {roleFields.includes('status') && <Status status={roleInformation.status as string} />}
          </Flex>
        </Flex>

        <Divider color="borderColor" />

        <Flex flexWrap="wrap" align="center" gap={2}>
          <HStack align="center" justify="center">
            <Center rounded="full">
              <CustomIconViewer variant="twoTone" icon={setRoleValueByKey('icon')} />
            </Center>

            <Stack spacing={1}>
              <TextWithCharacterLimit hasHighlight highlightQuery={setRoleFilterValueByKey('name')}>
                {setRoleValueByKey('name')}
              </TextWithCharacterLimit>

              <TextWithCharacterLimit
                variant="secondary"
                hasHighlight
                highlightQuery={setRoleFilterValueByKey('description')}
              >
                {setRoleValueByKey('description')}
              </TextWithCharacterLimit>
            </Stack>
          </HStack>

          {children && <Divider color="borderColor" />}

          {children}
        </Flex>
      </Stack>
    </Card>
  )
}

export default memo(RoleCard)
